import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
  Stack,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Add,
  Edit,
  Delete,
  PictureAsPdf,
  UploadFile,
  Search,
  RestartAlt,
  TableChart,
} from "@mui/icons-material";
import { format, isValid } from "date-fns";
import { MRT_ColumnDef } from "material-react-table";
import { useFormik } from "formik";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../common/StatusChip";
import { RowActions } from "../common/RowActions";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";
import { useTableState } from "../../hooks/useTableState";
import { Notify } from "../../layouts/notify";
import { supabase } from "../../utils/API";
import {
  PressRelease,
  useGetPressReleasesQuery,
  useAddPressReleaseMutation,
  useUpdatePressReleaseMutation,
  useDeletePressReleaseMutation,
} from "../../services/press-releases.query";

const validationSchema = Yup.object({
  alias: Yup.string().required("Title / Alias is required"),
  is_active: Yup.boolean().required("Status is required"),
});

export default function PressReleases() {
  const { filters, setFilters, resetFilters } = useTableState({
    search: "",
    page: 1,
  });

  const [searchQuery, setSearchQuery] = useState(filters.search || "");
  const page = filters.page || 1;
  const pageSize = 20;

  useEffect(() => {
    setSearchQuery(filters.search || "");
  }, [filters.search]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgressText, setExportProgressText] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState<PressRelease | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [pdfUploadError, setPdfUploadError] = useState<string>("");

  const {
    data: pressReleasesData = [],
    isLoading,
    isFetching,
    isError,
  } = useGetPressReleasesQuery();

  const [addPressRelease, { isLoading: addLoading }] = useAddPressReleaseMutation();
  const [updatePressRelease, { isLoading: updateLoading }] = useUpdatePressReleaseMutation();
  const [deletePressRelease, { isLoading: deleteLoading }] = useDeletePressReleaseMutation();

  const handleOpenAddModal = () => {
    setEditItem(null);
    setSelectedPdfFile(null);
    setPdfUploadError("");
    formik.resetForm({
      values: {
        alias: "",
        is_active: true,
      },
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (item: PressRelease) => {
    setEditItem(item);
    setSelectedPdfFile(null);
    setPdfUploadError("");
    formik.resetForm({
      values: {
        alias: item.alias || "",
        is_active: Boolean(item.is_active),
      },
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditItem(null);
    setSelectedPdfFile(null);
    setPdfUploadError("");
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate PDF file type
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        setPdfUploadError("Only PDF files are allowed.");
        setSelectedPdfFile(null);
        return;
      }

      setPdfUploadError("");
      setSelectedPdfFile(file);
    }
  };

  const uploadPdfFile = async (file: File): Promise<string> => {
    const fileExt = "pdf";
    const filePath = `press_releases/${uuidv4()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;

    // Try uploading to 'documents' bucket, fallback to 'images' bucket if needed
    let bucketName = "documents";
    let { data, error } = await supabase.storage.from(bucketName).upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

    if (error && (error.message.includes("Bucket not found") || error.message.includes("not found"))) {
      bucketName = "images";
      const retryResult = await supabase.storage.from(bucketName).upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
      data = retryResult.data;
      error = retryResult.error;
    }

    if (error) {
      throw new Error(error.message);
    }

    const { publicUrl } = supabase.storage.from(bucketName).getPublicUrl(filePath).data;
    return publicUrl;
  };

  const formik = useFormik({
    initialValues: {
      alias: "",
      is_active: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Validate PDF requirement for new entries
      if (!editItem && !selectedPdfFile) {
        setPdfUploadError("Please upload a PDF document.");
        return;
      }

      try {
        let pdfUrl = editItem?.file_url || "";

        if (selectedPdfFile) {
          pdfUrl = await uploadPdfFile(selectedPdfFile);
        }

        if (editItem && editItem.id) {
          await updatePressRelease({
            id: editItem.id,
            alias: values.alias.trim(),
            file_url: pdfUrl,
            is_active: values.is_active,
          }).unwrap();
          Notify("info", "Press Release updated successfully");
        } else {
          await addPressRelease({
            alias: values.alias.trim(),
            file_url: pdfUrl,
            is_active: values.is_active,
          }).unwrap();
          Notify("success", "Press Release added successfully");
        }

        handleCloseModal();
      } catch (error: any) {
        console.error("Error saving Press Release:", error);
        Notify("error", "Failed to save Press Release: " + (error?.message || error));
      }
    },
  });

  const handleDelete = async (id: number) => {
    try {
      await deletePressRelease(id).unwrap();
      Notify("info", "Press Release deleted successfully");
      setDeleteConfirmId(null);
    } catch (error: any) {
      console.error("Error deleting Press Release:", error);
      Notify("error", "Failed to delete Press Release");
    }
  };

  const hasActiveFilters = Boolean(filters.search);

  const handleApplySearch = () => {
    setFilters({ search: searchQuery.trim(), page: 1 });
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    resetFilters();
  };

  const filteredData = useMemo(() => {
    const q = (filters.search || "").toLowerCase().trim();
    if (!q) return pressReleasesData;
    return pressReleasesData.filter((item: PressRelease) =>
      item.alias?.toLowerCase().includes(q)
    );
  }, [filters.search, pressReleasesData]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      setExportProgressText("Preparing press releases PDF...");
      const doc = new jsPDF("landscape");
      doc.setFontSize(16);
      doc.text("MTRCB - Press Releases Report", 14, 15);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy hh:mm a")} | Total Records: ${filteredData.length}`,
        14,
        22
      );

      const tableRows = filteredData.map((item: any, index: number) => [
        index + 1,
        item.alias || "—",
        item.file_url ? "Available" : "No File",
        item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "—",
        item.is_active ? "ACTIVE" : "INACTIVE",
      ]);

      autoTable(doc, {
        head: [["#", "Title / Alias", "PDF Document", "Date Created", "Status"]],
        body: tableRows,
        startY: 27,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 114, 188] },
      });

      doc.save(`mtrcb-press-releases-${format(new Date(), "yyyyMMdd-HHmm")}.pdf`);
    } catch (err) {
      console.error(err);
      Notify("error", "Failed to export PDF.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      setExportProgressText("Preparing press releases Excel...");
      const rows = filteredData.map((item: any, index: number) => ({
        "No.": index + 1,
        "Title / Alias": item.alias || "",
        "Document URL": item.file_url || "",
        "Date Created": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "",
        Status: item.is_active ? "ACTIVE" : "INACTIVE",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Press Releases");
      XLSX.writeFile(
        workbook,
        `mtrcb-press-releases-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`
      );
    } catch (err) {
      console.error(err);
      Notify("error", "Failed to export Excel.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns: MRT_ColumnDef<PressRelease>[] = [
    {
      accessorKey: "number",
      header: "No.",
      size: 60,
      Cell: ({ row }) => (
        <Typography fontSize={14}>
          {(page - 1) * pageSize + row.index + 1}
        </Typography>
      ),
    },
    {
      accessorKey: "alias",
      header: "Title / Alias",
      size: 280,
      Cell: ({ cell }) => (
        <Typography fontSize={14} fontWeight={600} color="#111827">
          {cell.getValue<string>()}
        </Typography>
      ),
    },
    {
      accessorKey: "file_url",
      header: "PDF Document",
      size: 180,
      Cell: ({ cell }) => {
        const url = cell.getValue<string>();
        if (!url) return <Typography fontSize={14} color="text.secondary">—</Typography>;

        return (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdf color="error" />}
            onClick={() => window.open(url, "_blank")}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              fontWeight: 500,
              fontSize: 13,
            }}
          >
            View PDF
          </Button>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Date Created",
      size: 180,
      Cell: ({ cell }) => {
        const val = cell.getValue<string>();
        if (!val) return <Typography fontSize={14}>—</Typography>;
        const parsedDate = new Date(val);
        return (
          <Typography fontSize={14}>
            {isValid(parsedDate) ? format(parsedDate, "MMMM dd, yyyy") : "—"}
          </Typography>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Status",
      size: 130,
      Cell: ({ row }) => {
        const isActive = row.original.is_active;
        return (
          <StatusChip
            label={isActive ? "ACTIVE" : "INACTIVE"}
            tone={isActive ? "success" : "neutral"}
          />
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 160,
      enableSorting: false,
      Cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "Edit",
              onClick: () => handleOpenEditModal(row.original),
            },
            {
              label: "Delete",
              tone: "error",
              onClick: () => setDeleteConfirmId(row.original.id!),
            },
          ]}
          inline={2}
        />
      ),
    },
  ];

  return (
    <DefaultPage
      title="Press Releases"
      subTitle="Manage and review press releases."
      primaryButtonProps={{
        startIcon: <Add />,
        children: "Add New Press Release",
        onClick: handleOpenAddModal,
      }}
    >
      <ExportLoadingBackdrop
        open={isExporting}
        message={exportProgressText}
      />

      {/* Standardized Filter Container */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search Press Releases"
            placeholder="Search by title / alias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleApplySearch();
              }
            }}
            sx={{
              minWidth: { xs: "100%", sm: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
                fontSize: "13.5px",
              },
            }}
          />
        </Stack>

        <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Search />}
              onClick={handleApplySearch}
              disabled={isFetching}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 2,
                backgroundColor: "#0072BC",
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Search
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RestartAlt />}
                onClick={handleResetFilters}
                disabled={isFetching}
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  px: 2,
                  color: "#64748B",
                  borderColor: "#CBD5E1",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPdf}
              disabled={isExporting || filteredData.length === 0}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 2,
                color: "#334155",
                borderColor: "#CBD5E1",
                "&:hover": {
                  borderColor: "#94A3B8",
                  backgroundColor: "#F8FAFC",
                },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<TableChart />}
              onClick={handleExportExcel}
              disabled={isExporting || filteredData.length === 0}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                px: 2,
                backgroundColor: "#0072BC",
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Export Excel
            </Button>
          </Box>
        </Box>
      </Box>

      {isError && (
        <Box mb={2}>
          <Alert severity="error">
            Failed to load press releases.
          </Alert>
        </Box>
      )}

      <Box mb={2}>
        <Alert severity="success" sx={{ borderRadius: "6px" }}>
          Total matched records: <strong>{Number(filteredData.length || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive<PressRelease>
        data={paginatedData}
        total={filteredData.length}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setFilters({ page: newPage })}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRowClick={(row) => handleOpenEditModal(row.original)}
      />

      {/* Add / Edit Dialog Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
            p: 1,
          },
        }}
      >
        <DialogTitle fontWeight={700} fontSize={18}>
          {editItem ? "Edit Press Release" : "Add Press Release"}
        </DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Title / Alias"
                name="alias"
                placeholder="e.g. MTRCB Press Release 2026-001"
                value={formik.values.alias}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.alias && Boolean(formik.errors.alias)}
                helperText={
                  formik.touched.alias && (formik.errors.alias as string)
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                  },
                }}
              />

              <Box>
                <Typography fontSize={14} fontWeight={600} mb={1}>
                  PDF Document Upload
                </Typography>
                <input
                  accept="application/pdf,.pdf"
                  style={{ display: "none" }}
                  id="pdf_file_input"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="pdf_file_input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadFile />}
                    sx={{ borderRadius: "4px", textTransform: "none" }}
                  >
                    {selectedPdfFile
                      ? "Change PDF File"
                      : editItem?.file_url
                      ? "Replace PDF File"
                      : "Upload PDF Document"}
                  </Button>
                </label>

                {selectedPdfFile && (
                  <Typography fontSize={13} color="primary" mt={1}>
                    Selected: {selectedPdfFile.name}
                  </Typography>
                )}

                {!selectedPdfFile && editItem?.file_url && (
                  <Typography fontSize={13} color="text.secondary" mt={1}>
                    Current Document:{" "}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => window.open(editItem.file_url, "_blank")}
                      startIcon={<PictureAsPdf color="error" fontSize="small" />}
                      sx={{ borderRadius: "4px" }}
                    >
                      View Current PDF
                    </Button>
                  </Typography>
                )}

                {pdfUploadError && (
                  <Typography fontSize={12} color="error" mt={1}>
                    {pdfUploadError}
                  </Typography>
                )}
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderColor: "#E5E7EB",
                }}
              >
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    Active Status
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    Active press releases are visible on the mobile app.
                  </Typography>
                </Box>
                <Switch
                  checked={formik.values.is_active}
                  onChange={(e) =>
                    formik.setFieldValue("is_active", e.target.checked)
                  }
                  color="primary"
                />
              </Paper>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCloseModal}
              disabled={formik.isSubmitting || addLoading || updateLoading}
              sx={{ borderRadius: "4px", textTransform: "none" }}
            >
              Cancel
            </Button>

            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={formik.isSubmitting || addLoading || updateLoading}
              sx={{ borderRadius: "4px", px: 3, textTransform: "none" }}
            >
              {editItem ? "Update" : "Save"}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <DialogTitle fontWeight={600}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            Are you sure you want to delete this Press Release? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteConfirmId(null)}
            disabled={deleteLoading}
            sx={{ borderRadius: "4px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            loading={deleteLoading}
            sx={{ borderRadius: "4px", textTransform: "none" }}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </DefaultPage>
  );
}
